import toast from 'react-hot-toast';
import React, { useState } from 'react';
import { Button } from './ui/button';
import { Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ShowImageSelector = ({ showTitle, onImageSelected, onBackdropSelected, onGenresSelected, onReleaseDateSelected, onTmdbDataSelected, currentImageUrl, currentBackdropUrl }) => {
  const [updating, setUpdating] = useState(false);

  const updatePoster = async () => {
    if (!showTitle) {
      toast.error('Please enter a show title first');
      return;
    }
    
    setUpdating(true);
    try {
      // Call backend proxy endpoint
      const response = await axios.get(`${API}/search-tmdb`, {
        params: { q: showTitle },
        withCredentials: true
      });
      
      if (response.data && response.data.results && response.data.results.length > 0) {
        // Get the first (best) match
        const show = response.data.results[0];
        
        // Extract poster and backdrop URLs
        const posterUrl = show.poster_path 
          ? `https://image.tmdb.org/t/p/w500${show.poster_path}` 
          : null;
        const backdropUrl = show.backdrop_path 
          ? `https://image.tmdb.org/t/p/w780${show.backdrop_path}` 
          : null;
        
        // Extract genres and release date
        const genres = show.genres || [];
        const releaseDate = show.release_date || '';
        
        // Extract TMDB ID and media type for episode tracking
        const tmdbId = show.tmdb_id || null;
        const mediaType = show.media_type || 'tv';
        
        if (posterUrl) {
          onImageSelected(posterUrl);
          if (backdropUrl && onBackdropSelected) {
            onBackdropSelected(backdropUrl);
          }
          // Update genres and release date if callbacks provided
          if (genres.length > 0 && onGenresSelected) {
            onGenresSelected(genres);
          }
          if (releaseDate && onReleaseDateSelected) {
            onReleaseDateSelected(releaseDate);
          }
          // Update TMDB data for episode tracking
          if (tmdbId && onTmdbDataSelected) {
            onTmdbDataSelected(tmdbId, mediaType);
          }
          toast.success('Show details updated from TMDB!');
        } else {
          toast.error('No poster found for this show. Try a different spelling.');
        }
      } else {
        toast.error('No TV shows found with that name. Try a different spelling.');
      }
    } catch (error) {
      console.error('Error updating poster:', error);
      toast.error('Error updating poster. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="poster-updater">
      <div className="image-previews">
        <div className="preview-section">
          <label className="preview-label">Poster</label>
          {currentImageUrl ? (
            <div className="preview-container">
              <img src={currentImageUrl} alt="Current poster" className="poster-image" />
            </div>
          ) : (
            <div className="no-poster">
              <p>No poster selected</p>
            </div>
          )}
        </div>

        <div className="preview-section">
          <label className="preview-label">Background</label>
          {currentBackdropUrl ? (
            <div className="preview-container">
              <img src={currentBackdropUrl} alt="Current backdrop" className="backdrop-image" />
            </div>
          ) : (
            <div className="no-backdrop">
              <p>No background selected</p>
            </div>
          )}
        </div>
      </div>

      <Button
        type="button"
        onClick={updatePoster}
        disabled={updating || !showTitle}
        className="update-button"
        data-testid="update-poster-btn"
      >
        {updating ? (
          <>
            <Loader2 className="animate-spin mr-2" />
            Updating...
          </>
        ) : (
          'UPDATE SHOW DETAILS'
        )}
      </Button>

      <style jsx>{`
        .poster-updater {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          padding: 1rem;
          background: #1a1a1a;
          border: 1px solid #333333;
          border-radius: 8px;
        }

        .image-previews {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1.5rem;
        }

        .preview-section {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .preview-label {
          font-size: 0.9rem;
          font-weight: 600;
          color: #ffffff;
          text-align: center;
        }

        .preview-container {
          display: flex;
          justify-content: center;
        }

        .poster-image {
          width: 120px;
          height: 180px;
          object-fit: cover;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          border: 2px solid #333333;
        }

        .backdrop-image {
          width: 180px;
          height: 101px;
          object-fit: cover;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          border: 2px solid #333333;
        }

        .no-poster {
          width: 120px;
          height: 180px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #2a2a2a;
          border: 1px dashed #333333;
          border-radius: 8px;
          color: #666666;
        }

        .no-backdrop {
          width: 180px;
          height: 101px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #2a2a2a;
          border: 1px dashed #333333;
          border-radius: 8px;
          color: #666666;
        }

        .no-poster p,
        .no-backdrop p {
          margin: 0;
          font-size: 0.85rem;
          text-align: center;
          padding: 0.5rem;
        }

        .update-button {
          width: 100%;
        }

        @media (max-width: 768px) {
          .image-previews {
            grid-template-columns: 1fr;
            gap: 1rem;
          }

          .poster-image,
          .no-poster {
            width: 100%;
            max-width: 200px;
            height: auto;
            aspect-ratio: 2/3;
          }

          .backdrop-image,
          .no-backdrop {
            width: 100%;
            max-width: 300px;
            height: auto;
            aspect-ratio: 16/9;
          }
        }
      `}</style>
    </div>
  );
};

export default ShowImageSelector;
